/**
 * Countries, provinces/states and postal codes for Canada (the default) and the United States.
 * Pure data and functions with no dependencies, so the zod schemas here, the domain package and
 * the web app all share one definition. `@marketingbee/domain` re-exports everything in this file and
 * adds address formatting and Canadian sales tax (`packages/domain/src/common/address.ts`).
 */

// ---------------------------------------------------------------------------
// Regional defaults: BuildBee is a Canadian product
// ---------------------------------------------------------------------------

export const DEFAULT_COUNTRY = 'CA' as const;
export const DEFAULT_CURRENCY = 'CAD' as const;
export const DEFAULT_LOCALE = 'en-CA' as const;
export const DEFAULT_TIMEZONE = 'America/Toronto' as const;

// ---------------------------------------------------------------------------
// Countries
// ---------------------------------------------------------------------------

export const SUPPORTED_COUNTRIES = [
  { code: 'CA', name: 'Canada' },
  { code: 'US', name: 'United States' },
] as const;
export type SupportedCountry = (typeof SUPPORTED_COUNTRIES)[number]['code'];
export const SUPPORTED_COUNTRY_CODES = SUPPORTED_COUNTRIES.map((c) => c.code) as readonly SupportedCountry[];

export function isSupportedCountry(code: string | null | undefined): code is SupportedCountry {
  return code === 'CA' || code === 'US';
}

export function countryName(code: string | null | undefined): string | null {
  if (!code) return null;
  return SUPPORTED_COUNTRIES.find((c) => c.code === code.toUpperCase())?.name ?? null;
}

const COUNTRY_ALIASES: Record<string, SupportedCountry> = {
  CA: 'CA',
  CAN: 'CA',
  CANADA: 'CA',
  US: 'US',
  USA: 'US',
  UNITEDSTATES: 'US',
  UNITEDSTATESOFAMERICA: 'US',
  AMERICA: 'US',
};

/**
 * A country code from a code or name ("ca", "Canada", "USA", "United States"). Other two-letter
 * codes pass through uppercased (addresses outside CA/US stay free-form). Null when unrecognised.
 */
export function normalizeCountry(input: string | null | undefined): string | null {
  if (!input) return null;
  const key = foldKey(input);
  if (!key) return null;
  if (COUNTRY_ALIASES[key]) return COUNTRY_ALIASES[key];
  return /^[A-Z]{2}$/.test(key) ? key : null;
}

// ---------------------------------------------------------------------------
// Provinces, territories and states
// ---------------------------------------------------------------------------

export type Region = { code: string; name: string };

/** The 10 provinces and 3 territories (Canada Post two-letter symbols). */
export const CA_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
] as const satisfies readonly Region[];
export type CanadianProvince = (typeof CA_PROVINCES)[number]['code'];

/** The 50 states plus the District of Columbia (USPS codes). */
export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
] as const satisfies readonly Region[];
export type UsState = (typeof US_STATES)[number]['code'];

/** Provinces/states for a supported country, in display order; empty for other countries. */
export function regionsFor(country: string | null | undefined): readonly Region[] {
  const c = country?.toUpperCase();
  if (c === 'CA') return CA_PROVINCES;
  if (c === 'US') return US_STATES;
  return [];
}

/** Uppercase, accents removed, everything but letters and digits dropped ("Québec" -> "QUEBEC"). */
function foldKey(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

/** Common abbreviations and alternate names that are not the official code or name. */
const REGION_ALIASES: Record<SupportedCountry, Record<string, string>> = {
  CA: {
    ALTA: 'AB',
    BRITISHCOLUMBIA: 'BC',
    MAN: 'MB',
    NEWFOUNDLAND: 'NL',
    NFLD: 'NL',
    NF: 'NL',
    LABRADOR: 'NL',
    NWT: 'NT',
    ONT: 'ON',
    PEI: 'PE',
    PQ: 'QC',
    QUE: 'QC',
    SASK: 'SK',
    YUKONTERRITORY: 'YT',
    YK: 'YT',
  },
  US: {
    WASHINGTONDC: 'DC',
    DISTRICTOFCOLUMBIA: 'DC',
  },
};

const REGION_INDEX: Record<SupportedCountry, Map<string, string>> = {
  CA: buildIndex(CA_PROVINCES, REGION_ALIASES.CA),
  US: buildIndex(US_STATES, REGION_ALIASES.US),
};

function buildIndex(regions: readonly Region[], aliases: Record<string, string>): Map<string, string> {
  const index = new Map<string, string>();
  for (const r of regions) {
    index.set(r.code, r.code);
    index.set(foldKey(r.name), r.code);
  }
  for (const [alias, code] of Object.entries(aliases)) index.set(alias, code);
  return index;
}

/**
 * The two-letter province/state code for a code or full name in any case ("on", "Ontario",
 * "Québec", "british columbia", "wa", "Washington"). Null when the country is not CA/US or the
 * value is not a province/state of that country.
 */
export function normalizeRegion(country: string | null | undefined, input: string | null | undefined): string | null {
  const c = country?.toUpperCase();
  if (!input || !isSupportedCountry(c)) return null;
  return REGION_INDEX[c].get(foldKey(input)) ?? null;
}

export function regionName(country: string | null | undefined, code: string | null | undefined): string | null {
  if (!code) return null;
  return regionsFor(country).find((r) => r.code === code.toUpperCase())?.name ?? null;
}

// ---------------------------------------------------------------------------
// Postal codes
// ---------------------------------------------------------------------------

/**
 * Canadian postal code: letter digit letter, space, digit letter digit. D F I O Q U never appear,
 * and W and Z are never the first letter (Canada Post addressing guidelines).
 */
const CA_POSTAL = /^([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z])(\d[ABCEGHJ-NPRSTV-Z]\d)$/;
const US_ZIP = /^(\d{5})(?:[\s-]?(\d{4}))?$/;

/**
 * Canonical postal code, or null when invalid: Canada `A1A 1A1` (uppercase, one space; input may
 * be lowercase, unspaced or hyphenated); US `12345` or `12345-6789`.
 * Null for countries other than CA/US (their postal codes are free-form).
 */
export function normalizePostalCode(country: string | null | undefined, input: string | null | undefined): string | null {
  const c = country?.toUpperCase();
  if (!input) return null;
  if (c === 'CA') {
    const m = CA_POSTAL.exec(input.toUpperCase().replace(/[\s-]+/g, ''));
    return m ? `${m[1]} ${m[2]}` : null;
  }
  if (c === 'US') {
    const m = US_ZIP.exec(input.trim());
    return m ? (m[2] ? `${m[1]}-${m[2]}` : m[1]!) : null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** Label for a form that serves both countries before one is chosen. */
export const REGION_LABEL = 'Province/State';
export const POSTAL_LABEL = 'Postal/ZIP code';

/** "Province" for Canada, "State" for the US, "Province/State" when no country is known, "Region" otherwise. */
export function regionLabel(country?: string | null): string {
  const c = country?.toUpperCase();
  if (!c) return REGION_LABEL;
  if (c === 'CA') return 'Province';
  if (c === 'US') return 'State';
  return 'Region';
}

/** "Postal code" for Canada (and other countries), "ZIP code" for the US, "Postal/ZIP code" when no country is known. */
export function postalLabel(country?: string | null): string {
  const c = country?.toUpperCase();
  if (!c) return POSTAL_LABEL;
  return c === 'US' ? 'ZIP code' : 'Postal code';
}

/** Validation messages (shared by the zod schemas and the CSV import). */
export const ADDRESS_MESSAGES = {
  CA: {
    region: 'Choose a Canadian province or territory, e.g. ON',
    postalCode: 'Enter a valid Canadian postal code, e.g. M5V 2T6',
  },
  US: {
    region: 'Choose a US state, e.g. WA',
    postalCode: 'Enter a valid US ZIP code, e.g. 98101 or 98101-1234',
  },
} as const satisfies Record<SupportedCountry, { region: string; postalCode: string }>;
